import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Search,
    MapPin,
    Building2,
    Briefcase,
    Plus,
    Minus,
    Clock,
    IndianRupee
} from 'lucide-react';

const JobOpenings = () => {
    const [expandedFilters, setExpandedFilters] = useState({
        Industry: true,
        District: true,
        Company: true
    });

    const [hoveredJobId, setHoveredJobId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndustries, setSelectedIndustries] = useState([]);
    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);

    // State for dynamic data
    const [industries, setIndustries] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [companies, setCompanies] = useState([]);

    const toggleFilter = (section) => {
        setExpandedFilters(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Fetch industries, districts, and companies from API
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                // Fetch industries
                const industriesRes = await axios.get('/api/industries');
                setIndustries(industriesRes.data);

                // Fetch districts
                const districtsRes = await axios.get('/api/districts');
                setDistricts(districtsRes.data);

                // Fetch companies
                const companiesRes = await axios.get('/api/companies');
                setCompanies(companiesRes.data);
            } catch (error) {
                console.error('Error fetching filter data:', error);
            }
        };
        fetchFilters();
    }, []);

    // Toggle filter selection
    const toggleIndustryFilter = (industryId) => {
        setSelectedIndustries(prev =>
            prev.includes(industryId) ? prev.filter(id => id !== industryId) : [...prev, industryId]
        );
    };

    const toggleDistrictFilter = (districtId) => {
        setSelectedDistricts(prev =>
            prev.includes(districtId) ? prev.filter(id => id !== districtId) : [...prev, districtId]
        );
    };

    const toggleCompanyFilter = (companyId) => {
        setSelectedCompanies(prev =>
            prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
        );
    };

    // Static Data for Job Listings (sample data)
    const jobs = [
        {
            id: 1,
            title: "Production Manager",
            company: "ABC Steel Industries",
            companyLogo: "/img/aid.svg",
            industry: "Steel & Allied Products",
            industryColor: "#2196F3",
            location: "Nagpur",
            district: "Nagpur",
            description: "Oversee production operations, ensure quality standards, and manage production team for steel manufacturing unit.",
            experience: "5-8 years",
            salary: "8-12 LPA",
            type: "Full-time",
            postedDate: "Dec 05, 2025",
            openings: "3 positions"
        },
        {
            id: 2,
            title: "Agricultural Scientist",
            company: "Vidarbha Agro Ltd",
            companyLogo: "/img/aid.svg",
            industry: "Agro Processing & Agri Products",
            industryColor: "#4CAF50",
            location: "Akola",
            district: "Akola",
            description: "Research and develop innovative agricultural practices, work on crop improvement and soil management.",
            experience: "3-5 years",
            salary: "6-9 LPA",
            type: "Full-time",
            postedDate: "Dec 08, 2025",
            openings: "2 positions"
        },
        {
            id: 3,
            title: "Software Developer",
            company: "TechSoft Solutions",
            companyLogo: "/img/aid.svg",
            industry: "IT & IT Services",
            industryColor: "#9C27B0",
            location: "Nagpur",
            district: "Nagpur",
            description: "Develop and maintain web applications using modern frameworks. Work with cross-functional teams.",
            experience: "2-4 years",
            salary: "5-8 LPA",
            type: "Full-time",
            postedDate: "Dec 10, 2025",
            openings: "5 positions"
        },
        {
            id: 4,
            title: "Mining Engineer",
            company: "Vidarbha Mining Corp",
            companyLogo: "/img/aid.svg",
            industry: "Mining & Heavy Industries",
            industryColor: "#FF9800",
            location: "Chandrapur",
            district: "Chandrapur",
            description: "Plan and supervise mining operations, ensure safety protocols, and optimize extraction processes.",
            experience: "4-7 years",
            salary: "7-11 LPA",
            type: "Full-time",
            postedDate: "Dec 07, 2025",
            openings: "2 positions"
        },
        {
            id: 5,
            title: "Hotel Manager",
            company: "Heritage Resorts",
            companyLogo: "/img/aid.svg",
            industry: "Hospitality & Tourism",
            industryColor: "#E91E63",
            location: "Wardha",
            district: "Wardha",
            description: "Manage hotel operations, guest services, staff coordination, and ensure excellent customer experience.",
            experience: "6-10 years",
            salary: "6-10 LPA",
            type: "Full-time",
            postedDate: "Dec 09, 2025",
            openings: "1 position"
        },
        {
            id: 6,
            title: "Food Processing Technician",
            company: "Fresh Foods India",
            companyLogo: "/img/aid.svg",
            industry: "Food Processing",
            industryColor: "#FF5722",
            location: "Amravati",
            district: "Amravati",
            description: "Monitor food processing operations, ensure hygiene standards, and quality control in production.",
            experience: "1-3 years",
            salary: "3-5 LPA",
            type: "Full-time",
            postedDate: "Dec 06, 2025",
            openings: "4 positions"
        }
    ];

    // Filter jobs based on search and selected filters
    const filteredJobs = jobs.filter(job => {
        const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.description.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesIndustry = selectedIndustries.length === 0 || selectedIndustries.includes(job.industry);
        const matchesDistrict = selectedDistricts.length === 0 || selectedDistricts.includes(job.district);
        const matchesCompany = selectedCompanies.length === 0 || selectedCompanies.includes(job.company);

        return matchesSearch && matchesIndustry && matchesDistrict && matchesCompany;
    });

    return (
        <div style={{ fontFamily: '"Rubik", sans-serif', backgroundColor: "#fff", minHeight: "100vh", color: "#333", marginBottom: "60px" }}>

            {/* HERO SECTION WITH SPLIT DESIGN */}
            <div style={{ 
                position: "relative", 
                overflow: "hidden",
                background: "linear-gradient(135deg, #f8f9fa 0%, #fafbfc 100%)"
            }}>
                {/* Main Container */}
                <div style={{ 
                    maxWidth: "1400px", 
                    margin: "0 auto", 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr",
                    minHeight: "600px",
                    position: "relative"
                }}>
                    {/* Left Side - Content */}
                    <div style={{
                        padding: "80px 60px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        position: "relative",
                        zIndex: 2
                    }}>
                        {/* Badge */}
                        <div style={{
                            display: "inline-block",
                            width: "fit-content",
                            padding: "8px 20px",
                            background: "rgba(71, 1, 114, 0.1)",
                            borderRadius: "30px",
                            marginBottom: "25px"
                        }}>
                            <span style={{ 
                                fontSize: "14px", 
                                fontWeight: "600", 
                                color: "#470172",
                                letterSpacing: "0.5px"
                            }}>
                                 Join Vidarbha's Growth Story
                            </span>
                        </div>

                        {/* Main Heading */}
                        <h1 style={{ 
                            fontSize: "52px", 
                            fontWeight: "700", 
                            lineHeight: "1.2", 
                            color: "#1a202c",
                            marginBottom: "25px",
                            letterSpacing: "-1px",
                            textAlign: "center"
                        }}>
                            Your Next
                            <span style={{ 
                                display: "block",
                                background: "linear-gradient(135deg, #470172 0%, #764ba2 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}>
                                Career Opportunity
                            </span>
                            Awaits Here
                        </h1>

                        {/* Description */}
                        <p style={{ 
                            fontSize: "18px", 
                            color: "#4a5568", 
                            lineHeight: "1.8",
                            marginBottom: "40px",
                            maxWidth: "500px",
                            textAlign: "center"
                        }}>
                            Discover exciting roles across manufacturing, IT, agriculture, hospitality and more with Vidarbha's leading employers.
                        </p>

                        {/* Stats Cards */}
                        <div style={{ 
                            display: "grid", 
                            gridTemplateColumns: "repeat(2, 1fr)", 
                            gap: "20px",
                            marginBottom: "30px",
                            width: "100%",
                            maxWidth: "500px"
                        }}>
                            <div style={{
                                background: "#fff",
                                padding: "20px",
                                borderRadius: "12px",
                                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                                border: "1px solid #f0f0f0"
                            }}>
                                <div style={{ fontSize: "32px", fontWeight: "700", color: "#470172", marginBottom: "5px" }}>
                                    500+
                                </div>
                                <div style={{ fontSize: "14px", color: "#718096", fontWeight: "500" }}>
                                    Active Companies
                                </div>
                            </div>
                            <div style={{
                                background: "#fff",
                                padding: "20px",
                                borderRadius: "12px",
                                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                                border: "1px solid #f0f0f0"
                            }}>
                                <div style={{ fontSize: "32px", fontWeight: "700", color: "#470172", marginBottom: "5px" }}>
                                    1200+
                                </div>
                                <div style={{ fontSize: "14px", color: "#718096", fontWeight: "500" }}>
                                    Open Positions
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Images */}
                    <div style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "40px"
                    }}>
                        {/* Decorative Circle Background */}
                        <div style={{
                            position: "absolute",
                            width: "500px",
                            height: "500px",
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, rgba(71, 1, 114, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 0
                        }} />

                        {/* Image Grid */}
                        <div style={{
                            position: "relative",
                            zIndex: 1,
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "20px",
                            width: "100%",
                            maxWidth: "500px"
                        }}>
                            {/* Image 1 - Top Left */}
                            <div style={{
                                borderRadius: "20px",
                                overflow: "hidden",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                                transform: "translateY(30px)"
                            }}>
                                <img 
                                    src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&h=300&fit=crop" 
                                    alt="Team collaboration"
                                    style={{
                                        width: "100%",
                                        height: "250px",
                                        objectFit: "cover",
                                        display: "block"
                                    }}
                                />
                            </div>

                            {/* Image 2 - Top Right */}
                            <div style={{
                                borderRadius: "20px",
                                overflow: "hidden",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                                transform: "translateY(-20px)"
                            }}>
                                <img 
                                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop" 
                                    alt="Professional meeting"
                                    style={{
                                        width: "100%",
                                        height: "250px",
                                        objectFit: "cover",
                                        display: "block"
                                    }}
                                />
                            </div>

                            {/* Floating Badge */}
                            <div style={{
                                position: "absolute",
                                bottom: "20px",
                                right: "20px",
                                background: "#fff",
                                padding: "20px 25px",
                                borderRadius: "16px",
                                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                                border: "3px solid #470172"
                            }}>
                                <div style={{ fontSize: "24px", fontWeight: "700", color: "#470172", marginBottom: "5px" }}>
                                    11
                                </div>
                                <div style={{ fontSize: "13px", color: "#718096", fontWeight: "600" }}>
                                    Districts
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Highlights Bar */}
                <div style={{
                    maxWidth: "1100px",
                    margin: "10px auto 0",
                    marginBottom: "100px",
                    padding: "40px",
                    background: "linear-gradient(135deg, #470172 0%, #764ba2 100%)",
                    borderRadius: "20px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "30px",
                    boxShadow: "0 10px 40px rgba(71, 1, 114, 0.2)"
                }}>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "18px", fontWeight: "600", color: "#fff", marginBottom: "8px" }}>
                            ✓ Verified Employers
                        </div>
                        <div style={{ fontSize: "14px", color: "#f0e6f6" }}>
                            All companies thoroughly vetted
                        </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "18px", fontWeight: "600", color: "#fff", marginBottom: "8px" }}>
                            ✓ Direct Applications
                        </div>
                        <div style={{ fontSize: "14px", color: "#f0e6f6" }}>
                            Apply directly to employers
                        </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "18px", fontWeight: "600", color: "#fff", marginBottom: "8px" }}>
                            ✓ Local Opportunities
                        </div>
                        <div style={{ fontSize: "14px", color: "#f0e6f6" }}>
                            Focus on Vidarbha region
                        </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "18px", fontWeight: "600", color: "#fff", marginBottom: "8px" }}>
                            ✓ Regular Updates
                        </div>
                        <div style={{ fontSize: "14px", color: "#f0e6f6" }}>
                            Fresh jobs posted daily
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT SECTION */}
            <div style={{ backgroundColor: "#fcfcfc", padding: "40px 20px" }}>
                <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", gap: "30px", flexDirection: "row" }}>

                    {/* SIDEBAR FILTERS */}
                    <div style={{ width: "260px", flexShrink: 0 }}>
                        <h3 style={{ fontSize: "24px", fontWeight: "500", marginBottom: "25px", color: "#470172" }}>Filters</h3>

                        <div style={{ backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.03)", border: "1px solid #eee" }}>
                            {/* Industry Filter */}
                            <div style={{ borderBottom: "1px solid #f0f0f0" }}>
                                <div
                                    onClick={() => toggleFilter('Industry')}
                                    style={{ padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                >
                                    <span style={{ fontWeight: "500", fontSize: "16px", color: "#4a5568" }}>Industry</span>
                                    {expandedFilters.Industry ? <Minus size={18} color="#ed8936" /> : <Plus size={18} color="#ed8936" />}
                                </div>
                                {expandedFilters.Industry && (
                                    <div style={{ padding: "0 20px 20px" }}>
                                        {industries.length > 0 ? (
                                            industries.map((industry) => (
                                                <div key={industry._id} style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        id={`industry-${industry._id}`}
                                                        checked={selectedIndustries.includes(industry.name)}
                                                        onChange={() => toggleIndustryFilter(industry.name)}
                                                        style={{ marginRight: "8px", cursor: "pointer" }}
                                                    />
                                                    <label htmlFor={`industry-${industry._id}`} style={{ fontSize: "14px", color: "#4a5568", cursor: "pointer" }}>
                                                        {industry.name}
                                                    </label>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ color: "#718096", fontSize: "14px" }}>Loading industries...</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* District Filter */}
                            <div style={{ borderBottom: "1px solid #f0f0f0" }}>
                                <div
                                    onClick={() => toggleFilter('District')}
                                    style={{ padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                >
                                    <span style={{ fontWeight: "500", fontSize: "16px", color: "#4a5568" }}>District</span>
                                    {expandedFilters.District ? <Minus size={18} color="#ed8936" /> : <Plus size={18} color="#ed8936" />}
                                </div>
                                {expandedFilters.District && (
                                    <div style={{ padding: "0 20px 20px" }}>
                                        {districts.length > 0 ? (
                                            districts.map((district) => (
                                                <div key={district._id} style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        id={`district-${district._id}`}
                                                        checked={selectedDistricts.includes(district.name)}
                                                        onChange={() => toggleDistrictFilter(district.name)}
                                                        style={{ marginRight: "8px", cursor: "pointer" }}
                                                    />
                                                    <label htmlFor={`district-${district._id}`} style={{ fontSize: "14px", color: "#4a5568", cursor: "pointer" }}>
                                                        {district.name}
                                                    </label>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ color: "#718096", fontSize: "14px" }}>Loading districts...</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Company Filter */}
                            <div>
                                <div
                                    onClick={() => toggleFilter('Company')}
                                    style={{ padding: "18px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                                >
                                    <span style={{ fontWeight: "500", fontSize: "16px", color: "#4a5568" }}>Company</span>
                                    {expandedFilters.Company ? <Minus size={18} color="#ed8936" /> : <Plus size={18} color="#ed8936" />}
                                </div>
                                {expandedFilters.Company && (
                                    <div style={{ padding: "0 20px 20px" }}>
                                        {companies.length > 0 ? (
                                            companies.map((company) => (
                                                <div key={company._id} style={{ marginBottom: "10px", display: "flex", alignItems: "center" }}>
                                                    <input
                                                        type="checkbox"
                                                        id={`company-${company._id}`}
                                                        checked={selectedCompanies.includes(company.name)}
                                                        onChange={() => toggleCompanyFilter(company.name)}
                                                        style={{ marginRight: "8px", cursor: "pointer" }}
                                                    />
                                                    <label htmlFor={`company-${company._id}`} style={{ fontSize: "14px", color: "#4a5568", cursor: "pointer" }}>
                                                        {company.name}
                                                    </label>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ color: "#718096", fontSize: "14px" }}>Loading companies...</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* JOB LISTINGS */}
                    <div style={{ flex: 1 }}>

                        {/* Header: Count & Search */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                            <h2 style={{ fontSize: "28px", fontWeight: "500", color: "#470172" }}>
                                <span style={{ color: "#470172", fontWeight: "700" }}>{filteredJobs.length}</span> Job{filteredJobs.length !== 1 ? 's' : ''} Found
                            </h2>
                            <div style={{ position: "relative", width: "320px" }}>
                                <input
                                    type="text"
                                    placeholder="Search Jobs"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: "100%",
                                        padding: "12px 15px",
                                        borderRadius: "8px",
                                        border: "1px solid #e2e8f0",
                                        fontSize: "15px",
                                        outline: "none",
                                        fontFamily: '"Rubik", sans-serif'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Jobs Grid */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "30px" }}>
                            {filteredJobs.map((job) => (
                                <div
                                    key={job.id}
                                    style={{
                                        position: "relative",
                                        height: "100%",
                                        zIndex: hoveredJobId === job.id ? 10 : 1
                                    }}
                                    onMouseEnter={() => setHoveredJobId(job.id)}
                                    onMouseLeave={() => setHoveredJobId(null)}
                                >
                                    {/* CARD CONTAINER */}
                                    <div style={{
                                        backgroundColor: "#fff",
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        boxShadow: hoveredJobId === job.id ? "0 20px 40px rgba(0,0,0,0.15)" : "0 4px 10px rgba(0,0,0,0.05)",
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
                                        cursor: "pointer",
                                        border: "1px solid #eee",
                                        position: hoveredJobId === job.id ? "absolute" : "relative",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: hoveredJobId === job.id ? "scale(1.05) translateY(-10px)" : "scale(1)",
                                        minHeight: "100%"
                                    }}>
                                        {/* Header Section */}
                                        <div style={{ padding: "20px", borderBottom: "1px solid #f0f0f0" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "12px" }}>
                                                <div style={{ flex: 1 }}>
                                                    <span style={{
                                                        fontSize: "12px",
                                                        fontWeight: "600",
                                                        color: job.industryColor,
                                                        marginBottom: "8px",
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px",
                                                        display: "block"
                                                    }}>
                                                        {job.industry}
                                                    </span>
                                                    <h3 style={{
                                                        fontSize: "18px",
                                                        fontWeight: "600",
                                                        color: "#2d3748",
                                                        marginBottom: "8px",
                                                        lineHeight: "1.4"
                                                    }}>
                                                        {job.title}
                                                    </h3>
                                                </div>
                                                <img src={job.companyLogo} alt="Company Logo" style={{ height: "30px", marginLeft: "10px" }} />
                                            </div>

                                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                                                <Building2 size={14} color="#718096" />
                                                <span style={{ fontSize: "14px", color: "#4a5568", fontWeight: "500" }}>{job.company}</span>
                                            </div>

                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <MapPin size={14} color="#718096" />
                                                <span style={{ fontSize: "14px", color: "#718096" }}>{job.location}, {job.district}</span>
                                            </div>
                                        </div>

                                        {/* Content Section */}
                                        <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column" }}>
                                            <p style={{
                                                fontSize: "14px",
                                                color: "#718096",
                                                lineHeight: "1.6",
                                                marginBottom: "20px",
                                                display: "-webkit-box",
                                                WebkitLineClamp: hoveredJobId === job.id ? 6 : 3,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                flex: 1
                                            }}>
                                                {job.description}
                                            </p>

                                            {/* Expanded Info - Only visible on hover */}
                                            {hoveredJobId === job.id && (
                                                <div style={{
                                                    marginBottom: "15px",
                                                    padding: "12px",
                                                    backgroundColor: "#f7fafc",
                                                    borderRadius: "8px",
                                                    fontSize: "13px"
                                                }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                                                        <Briefcase size={14} color="#470172" />
                                                        <span style={{ color: "#4a5568" }}><strong>Experience:</strong> {job.experience}</span>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                                                        <IndianRupee size={14} color="#470172" />
                                                        <span style={{ color: "#4a5568" }}><strong>Salary:</strong> {job.salary}</span>
                                                    </div>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                        <Clock size={14} color="#470172" />
                                                        <span style={{ color: "#4a5568" }}><strong>Type:</strong> {job.type}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Footer Info */}
                                            <div style={{
                                                borderTop: "1px solid #edf2f7",
                                                paddingTop: "15px",
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                color: "#718096",
                                                fontSize: "13px",
                                                fontWeight: "500"
                                            }}>
                                                <span>{job.openings}</span>
                                                <span style={{ color: "#a0aec0" }}>{job.postedDate}</span>
                                            </div>

                                            {/* Apply Button - Only visible on hover */}
                                            {hoveredJobId === job.id && (
                                                <button style={{
                                                    marginTop: "15px",
                                                    padding: "10px 20px",
                                                    backgroundColor: "#470172",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    fontSize: "14px",
                                                    fontWeight: "600",
                                                    cursor: "pointer",
                                                    transition: "background-color 0.2s ease"
                                                }}
                                                    onMouseOver={(e) => e.target.style.backgroundColor = "#5a0190"}
                                                    onMouseOut={(e) => e.target.style.backgroundColor = "#470172"}
                                                >
                                                    Apply Now
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {/* Placeholder to keep layout stable */}
                                    {hoveredJobId === job.id && <div style={{ height: "480px" }}></div>}
                                </div>
                            ))}
                        </div>

                        {/* No Results Message */}
                        {filteredJobs.length === 0 && (
                            <div style={{ textAlign: "center", padding: "60px 20px" }}>
                                <p style={{ fontSize: "18px", color: "#718096" }}>No jobs found matching your criteria. Try adjusting your filters.</p>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </div>
    );
};

export default JobOpenings;
